import {
  signIn,
  signInWithRedirect,
  signOut,
  resendSignUpCode,
  updatePassword,
  fetchAuthSession,
  confirmSignIn,
  resetPassword,
  confirmSignUp,
  confirmResetPassword,
} from 'aws-amplify/auth'
import i18n from 'utils/i18n'
import { LoginFormValues } from 'app/components/pages/login/models'
import { ChangePasswordFormValues, SignInResult, BooleanResult } from './models'
import { Amplify } from 'aws-amplify'
import { authConfig } from './config'

export const handleSignIn = async (values: LoginFormValues): Promise<SignInResult> => {
  try {
    const email = values.email.toLowerCase()

    const { nextStep, isSignedIn } = await signIn({
      username: String(email),
      password: String(values.password),
    })
    if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return { success: true, newPasswordRequired: true }
    }
    if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
      await resendSignUpCode({
        username: String(email),
      })
      return { success: true, redirectTo: '/confirmUser' }
    }

    return { success: isSignedIn }
  } catch (error: any) {
    console.log('Error', error)
    let errorMessage
    if (error.code === 'UserNotConfirmedException') {
      errorMessage = i18n.t('common:accountNotConfirmed')
    } else if (error.code === 'NotAuthorizedException' || error.code === 'UserNotFoundException') {
      errorMessage = i18n.t('common:invalidLoginCredentials')
    } else {
      errorMessage = i18n.t('error')
    }

    return { success: false, errorMessage: errorMessage }
  }
}

export const handleSignInWithRedirect = async (
  provider: any | undefined,
  customProvider?: string,
): Promise<BooleanResult> => {
  try {
    if (provider) {
      await signInWithRedirect({ provider: provider })
    } else if (customProvider) {
      await signInWithRedirect({ provider: { custom: 'Vipps' } })
    } else {
      throw new Error('Unknown provider')
    }

    return { success: true }
  } catch (error: any) {
    console.log('Error', error)
    return { success: false, errorMessage: i18n.t('error') }
  }
}

export const handleSignOut = async (): Promise<BooleanResult> => {
  try {
    await signOut()
    return { success: true }
  } catch (error) {
    console.log(`Error on sign out`, error)
    const errorMessage = i18n.t('error')
    return { success: false, errorMessage: errorMessage }
  }
}

export const handleChangePassword = async (values: ChangePasswordFormValues): Promise<BooleanResult> => {
  try {
    await updatePassword({
      oldPassword: values.oldPassword,
      newPassword: values.password,
    })
    return { success: true }
  } catch (error: any) {
    console.log(`Error on password change`, error)
    let errorMessage
    if (error.code === 'NotAuthorizedException') {
      // The error happens when the old incorrect password is provided
      errorMessage = i18n.t('auth:oldPasswordInvalid')
    } else {
      errorMessage = i18n.t('error')
    }
    return { success: false, errorMessage: errorMessage }
  }
}

export const handleConfirmSignIn = async (newPassword: string): Promise<BooleanResult> => {
  try {
    const { isSignedIn } = await confirmSignIn({ challengeResponse: newPassword })
    return { success: isSignedIn }
  } catch (error: any) {
    console.log(`Error on password change`, error)
    return { success: false, errorMessage: i18n.t('error') }
  }
}

export const handleSignOutIfNeeded = async (): Promise<BooleanResult> => {
  try {
    const existingUser = await fetchAuthSession()
    if (existingUser.userSub) {
      return handleSignOut()
    }
    return { success: true }
  } catch (error) {
    console.log(`Error on checking is user logged in`, error)
    const errorMessage = i18n.t('error')
    return { success: false, errorMessage: errorMessage }
  }
}

export const handleResetPassword = async (username: string): Promise<BooleanResult> => {
  try {
    console.log(`handleResetPassword`, username)
    await resetPassword({
      username: username,
    })
    return { success: true }
  } catch (error: any) {
    console.log(`Error on password change`, error)
    let errorMessage
    if (error.code === 'LimitExceededException') {
      errorMessage = i18n.t('auth:limitExceededException')
    } else {
      errorMessage = i18n.t('error')
    }
    return { success: false, errorMessage: errorMessage }
  }
}

export const handleConfirmSignUp = async (clientId: string, username: string, code: string): Promise<BooleanResult> => {
  if (!clientId) {
    return { success: false, errorMessage: i18n.t('error') }
  }

  try {
    const { isSignUpComplete } = await confirmSignUp({
      username,
      confirmationCode: code,
    })
    return { success: isSignUpComplete }
  } catch (error: any) {
    console.log(`Error on handleConfirmSignUp`, error)
    return { success: false, errorMessage: i18n.t('error') }
  }
}

export const getIsUserAuthenticated = async () => {
  if (typeof window === 'undefined') {
    console.error('getIsUserAuthenticated called on server side')
    return false // Doesn't work for SSR
  }

  try {
    Amplify.configure(
      {
        Auth: authConfig,
      },
      { ssr: true },
    )
    const session = await fetchAuthSession()
    return session.tokens !== undefined
  } catch (err) {
    console.error('Auth check failed:', err)
    return false
  }
}

export const handleConfirmResetPassword = async (request: {
  email: string
  code: string
  password: string
}): Promise<BooleanResult> => {
  try {
    const lowerEmail = request.email.toLowerCase()

    await confirmResetPassword({
      username: lowerEmail,
      newPassword: request.password,
      confirmationCode: request.code,
    })
    return { success: true }
  } catch (error: any) {
    console.log(`Error on handleConfirmSignUp`, error)
    let errorMessage
    if (error.code === 'CodeMismatchException') {
      errorMessage = i18n.t('auth:codeMismatch')
    } else if (error.code === 'InvalidPasswordException') {
      errorMessage = i18n.t('auth:incorrectPassword')
    } else {
      errorMessage = i18n.t('error')
    }

    return { success: false, errorMessage: errorMessage }
  }
}
